import { StoreProduct, DbStoreProduct, DbStoreProductInsert, DbStoreProductUpdate } from '@/types/entities/store-product';

/**
 * Маппер из БД в frontend (snake_case -> camelCase)
 * Обрабатывает фактическую структуру данных из Supabase
 */
type LegacyDbStoreProduct = DbStoreProduct & {
  // Дополнительные возможные поля из старых версий схемы
  cost_price?: number | null;
  min_quantity?: number | null;
};

export function mapDbStoreProductToStoreProduct(dbProduct: LegacyDbStoreProduct): StoreProduct {
  return {
    id: dbProduct.id,
    userId: dbProduct.user_id,
    createdAt: new Date(dbProduct.created_at || new Date().toISOString()),
    updatedAt: new Date(dbProduct.updated_at || new Date().toISOString()),
    name: dbProduct.name || '',
    description: dbProduct.description || null,
    categoryId: dbProduct.category_id || null,
    rentalPointId: dbProduct.rental_point_id || null,
    price: dbProduct.price || null,
    costPrice: dbProduct.cost ?? dbProduct.cost_price ?? null,
    quantity: dbProduct.quantity || null,
    minQuantity: dbProduct.min_stock_level ?? dbProduct.min_quantity ?? null,
    sku: dbProduct.sku || null,
    barcode: dbProduct.barcode || null,
    isActive: dbProduct.is_active !== undefined ? dbProduct.is_active : true,
    expirationDate: dbProduct.expiration_date || null,
    sortOrder: 0, // Default value since not in DB schema
    // Backward compatibility fields
    stock: dbProduct.stock ?? dbProduct.quantity ?? null, // Use stock if available, fallback to quantity
    unit: dbProduct.unit || 'шт' // Default unit
  };
}

/**
 * Маппер из frontend в БД для создания (camelCase -> snake_case)
 */
export function mapStoreProductToDbInsert(product: Partial<StoreProduct>, currentUserId?: string): DbStoreProductInsert {
  // Проверяем, что rental_point_id не пустой
  if (!product.rentalPointId || product.rentalPointId.trim() === '') {
    throw new Error('rental_point_id is required and cannot be empty');
  }
  
  const result = {
    user_id: product.userId && product.userId !== 'unassigned' ? product.userId : currentUserId || product.userId || '',
    rental_point_id: product.rentalPointId, // Обязательное поле - должно быть валидным UUID
    name: product.name || '',
    description: product.description || null,
    category_id: product.categoryId && product.categoryId !== '' ? product.categoryId : null,
    price: product.price !== undefined ? product.price : null,
    cost: product.costPrice !== undefined ? product.costPrice : null,
    quantity: product.quantity !== undefined ? product.quantity : null,
    min_stock_level: product.minQuantity !== undefined ? product.minQuantity : null,
    barcode: product.barcode || null,
    sku: product.sku || null,
    is_active: product.isActive !== undefined ? product.isActive : true,
    expiration_date: product.expirationDate || null,
    // Backward compatibility fields
    stock: product.stock !== undefined ? product.stock : (product.quantity !== undefined ? product.quantity : null),
    unit: product.unit || 'шт'
  } as DbStoreProductInsert;
  return result;
}

/**
 * Маппер из frontend в БД для обновления (camelCase -> snake_case)
 */
export function mapStoreProductUpdateToDbUpdate(product: Partial<StoreProduct>): DbStoreProductUpdate {
  return {
    name: product.name !== undefined ? product.name : undefined,
    category_id: product.categoryId !== undefined ? product.categoryId : undefined,
    price: product.price !== undefined ? product.price : undefined,
    cost: product.costPrice !== undefined ? product.costPrice : undefined,
    quantity: product.quantity !== undefined ? product.quantity : undefined,
    expiration_date: product.expirationDate !== undefined ? product.expirationDate : undefined,
    // Backward compatibility fields
    stock: product.stock !== undefined ? product.stock : (product.quantity !== undefined ? product.quantity : undefined),
    unit: product.unit !== undefined ? product.unit : undefined
  };
}
